// AddAssemblyModal.js — "+ Add Assembly" on Build New Material.
//
// 1. The full-size Search Materials window opens straight away (all approved materials, or a "My
//    approved materials" tab), grouped by category with text / category / describe search.
// 2. Picking one moves to a short step: how much of it goes into 1 unit of the material being built,
//    with a preview of what it adds. "Add & pick another" goes back to the search to combine more.
// Its line items are copied in — scaled, under a heading of its own. See utils/expandAssembly.js for
// why it's a copy rather than a stored link.

import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import MaterialSearchModal from './MaterialSearchModal';
import { breakdownToRows } from '../utils/expandAssembly';
import { unitKind, unitInfo, fmt } from '../utils/conversionRecipes';
import './PageLayout.css';

const S = {
  card: { maxWidth: 720, width: '92%', textAlign: 'left', padding: '24px 28px' },
  title: { margin: '0 0 4px', fontSize: 20 },
  intro: { margin: '0 0 16px', color: '#64748b', fontSize: 14, lineHeight: 1.5 },
  picked: {
    display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between',
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px',
  },
  pickedName: { fontWeight: 700, fontSize: 15, color: '#0f172a' },
  pickedMeta: { fontSize: 12.5, color: '#64748b', marginTop: 2 },
  link: { background: 'none', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 600, color: '#334155' },
  label: { display: 'block', fontWeight: 600, fontSize: 14, color: '#1e293b', margin: '18px 0 6px' },
  row: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  qty: { width: 140, padding: '10px 12px', border: '1px solid #cfd8dc', borderRadius: 8, textAlign: 'right', fontSize: 15, marginBottom: 0 },
  hint: { margin: '8px 0 0', fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 10px' },
  preview: { margin: '16px 0 0', background: '#f0fdfa', border: '1px solid #99d5cc', borderRadius: 10, padding: '12px 14px', fontSize: 14 },
  previewHead: { fontWeight: 700, color: '#134e4a', marginBottom: 4 },
  error: { margin: '12px 0 0', color: '#b91c1c', fontSize: 13.5, fontWeight: 600 },
  done: { margin: '12px 0 0', color: '#047857', fontSize: 13.5, fontWeight: 600 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22, flexWrap: 'wrap' },
};

const sumPack = (rows, key) => rows.reduce((s, r) => s + (Number(r[key]?.total) || 0), 0);

function AddAssemblyModal({ isOpen, onClose, templateOptions, compositeUnit, loadBuildUp, onAdd, categoryFor, isMine }) {
  const [step, setStep] = useState('search'); // 'search' (the big Search Materials window) | 'amount'
  const [picked, setPicked] = useState(null);
  const [scale, setScale] = useState('1');
  const [source, setSource] = useState(null); // { breakdown, lineItems } of the picked assembly
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStep('search'); setPicked(null); setScale('1'); setSource(null); setError(''); setAdded('');
  }, [isOpen]);

  const tabs = useMemo(() => [
    { key: 'all', label: 'All Materials' },
    {
      key: 'mine',
      label: 'My Approved Materials',
      filter: (m) => !!isMine && isMine(m),
      empty: "You haven't had any materials approved in this region yet.",
    },
  ], [isMine]);

  const pick = async (raw) => {
    setPicked(raw || null);
    setSource(null);
    setError('');
    setStep('amount');
    if (!raw) return;
    setLoading(true);
    try {
      const buildUp = await loadBuildUp(raw.Id);
      if (!buildUp?.breakdown?.length) {
        setError(`"${raw.Name}" has no build-up to add (it may be cost only).`);
      } else {
        setSource(buildUp);
      }
    } catch (e) {
      setError(`Couldn't load "${raw.Name}": ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const k = Number(scale);
  const scaleOk = Number.isFinite(k) && k > 0;

  // the rows that will be added, recomputed as the amount changes (ids are throwaway here)
  const preview = useMemo(() => {
    if (!source || !scaleOk || !picked) return null;
    let i = 0;
    const out = breakdownToRows({
      breakdown: source.breakdown, lineItems: source.lineItems, scale: k,
      block: { blockId: 'preview', id: picked.Id, name: picked.Name }, uuid: () => `p${++i}`, categoryFor,
    });
    const data = out.rows.filter(r => !r.isHeading && !r.isSubHeading);
    return { count: data.length, carbon: sumPack(data, 'carbon'), bio: sumPack(data, 'bio'), weight: sumPack(data, 'weight') };
  }, [source, scaleOk, k, picked, categoryFor]);

  const parentUnit = unitInfo(compositeUnit)?.label || 'unit';
  const childUnit = unitInfo(picked?.QuantityTypeName)?.label || picked?.QuantityTypeName || '';
  const unitsDiffer = picked && compositeUnit && picked.QuantityTypeName
    && unitKind(compositeUnit) && unitKind(picked.QuantityTypeName)
    && unitKind(compositeUnit) !== unitKind(picked.QuantityTypeName);

  const add = (another) => {
    if (!source || !scaleOk || !picked) return;
    const blockId = uuidv4();
    const built = breakdownToRows({
      breakdown: source.breakdown, lineItems: source.lineItems, scale: k,
      block: { blockId, id: picked.Id, name: picked.Name }, uuid: uuidv4, categoryFor,
    });
    onAdd({ ...built, name: picked.Name, scale: k });
    if (another) {
      setAdded(`Added "${picked.Name}" × ${fmt(k)}.`);
      setPicked(null); setSource(null); setScale('1'); setError('');
      setStep('search');
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  if (step === 'search') {
    return (
      <MaterialSearchModal
        isOpen
        onClose={onClose}
        onSelect={(name, cat, raw) => pick(raw)}
        materials={templateOptions}
        showMetrics={false}
        tabs={tabs}
        title={added ? `Add an assembly — ${added} Pick another, or Cancel when done` : 'Add an assembly — pick one to copy in'}
      />
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-assembly-title">
      <div className="modal-content" style={S.card}>
        <h3 id="add-assembly-title" style={S.title}>Add an assembly</h3>
        <p style={S.intro}>
          Its line items are copied into this material — scaled, under a heading with its name. Add several to
          combine them. Later changes to that assembly won't flow into this one.
        </p>

        {picked && (
          <div style={S.picked}>
            <div>
              <div style={S.pickedName}>{picked.Name}</div>
              <div style={S.pickedMeta}>{[picked.Category, childUnit].filter(Boolean).join(' · ')}</div>
            </div>
            <button type="button" style={S.link} onClick={() => setStep('search')}>Change</button>
          </div>
        )}

        {picked && (
          <>
            <label style={S.label} htmlFor="assembly-scale">
              How much {childUnit ? `(${childUnit}) ` : ''}per 1 {parentUnit} of this material
            </label>
            <div style={S.row}>
              <input
                id="assembly-scale"
                type="text"
                inputMode="decimal"
                style={S.qty}
                value={scale}
                onChange={(e) => { setScale(e.target.value); setAdded(''); }}
              />
              <span style={{ fontSize: 14, color: '#64748b' }}>× {picked.Name}</span>
            </div>
            {unitsDiffer && (
              <p style={S.hint}>
                ⚠️ This assembly is measured in {childUnit}, but you're building per {parentUnit} — enter how many {childUnit}
                {' '}go into 1 {parentUnit} (the Quantity Calculator's Conversion helper can work it out).
              </p>
            )}
          </>
        )}

        {loading && <p style={S.intro}>Loading its build-up…</p>}
        {error && <p style={S.error}>{error}</p>}
        {preview && (
          <div style={S.preview}>
            <div style={S.previewHead}>Adds {preview.count} line item{preview.count === 1 ? '' : 's'} under “{picked.Name} × {fmt(k)}”</div>
            <div>Carbon {fmt(preview.carbon)} · Biogenic {fmt(preview.bio)} · Weight {fmt(preview.weight)} — per 1 {parentUnit}</div>
          </div>
        )}
        {!scaleOk && picked && <p style={S.error}>Enter an amount greater than 0.</p>}

        <div style={S.actions}>
          <button type="button" className="quantity-calc-buttons" onClick={onClose}>Cancel</button>
          <button type="button" className="quantity-calc-buttons" disabled={!preview} onClick={() => add(true)}>Add &amp; pick another</button>
          <button type="button" className="quantity-calc-buttons" disabled={!preview} onClick={() => add(false)}>Add assembly</button>
        </div>
      </div>
    </div>
  );
}

export default AddAssemblyModal;
